/**
 * Google Tag Manager / GA4 Analytics Plugin
 * 
 * Isolated plugin responsible solely for pushing standard GA4 e-commerce
 * events to window.dataLayer.
 */

import { AnalyticsPlugin, AnalyticsEventPayload } from '../types';

export class GtmPlugin implements AnalyticsPlugin {
  readonly name = 'gtm';

  isReady(): boolean {
    return typeof window !== 'undefined' && Array.isArray(window.dataLayer);
  }

  private getDataLayer(): any[] {
    if (typeof window === 'undefined') return [];
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }

  track(eventName: string, payload: AnalyticsEventPayload): void {
    if (typeof window === 'undefined') return;

    const dl = this.getDataLayer();

    // Map unified events to GA4 e-commerce format
    if (eventName === 'Purchase') {
      dl.push({ ecommerce: null }); // Clear previous ecommerce object
      dl.push({
        event: 'purchase',
        ecommerce: {
          transaction_id: payload.order_id || payload.event_id,
          value: payload.value || 0,
          currency: payload.currency || 'BDT',
          items: (payload.contents || []).map((item) => ({
            item_id: String(item.content_id),
            item_name: item.content_name || '',
            price: item.price || 0,
            quantity: item.quantity || 1,
          })),
        },
      });
    } else if (eventName === 'AddToCart') {
      dl.push({ ecommerce: null });
      dl.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: payload.currency || 'BDT',
          value: payload.value || 0,
          items: (payload.contents || []).map((item) => ({
            item_id: String(item.content_id),
            item_name: item.content_name || '',
            price: item.price || 0,
            quantity: item.quantity || 1,
          })),
        },
      });
    } else if (eventName === 'ViewContent') {
      dl.push({ ecommerce: null });
      dl.push({
        event: 'view_item',
        ecommerce: {
          currency: payload.currency || 'BDT',
          value: payload.value || 0,
          items: (payload.contents || []).map((item) => ({
            item_id: String(item.content_id),
            item_name: item.content_name || '',
            price: item.price || 0,
            quantity: item.quantity || 1,
          })),
        },
      });
    } else if (eventName === 'InitiateCheckout') {
      dl.push({ ecommerce: null });
      dl.push({
        event: 'begin_checkout',
        ecommerce: {
          currency: payload.currency || 'BDT',
          value: payload.value || 0,
          items: (payload.contents || []).map((item) => ({
            item_id: String(item.content_id),
            item_name: item.content_name || '',
            price: item.price || 0,
            quantity: item.quantity || 1,
          })),
        },
      });
    } else {
      // Custom or general event
      dl.push({
        event: eventName,
        event_data: payload,
      });
    }
  }
}
